/**
 * #987 — Polymarket US positions, orders and fills for the trader being viewed.
 *
 * Same convention as the Kalshi and Polymarket panels above it: the manager
 * or admin role in the org is the authority (manager routes, org-scoped), and
 * the rows are the selected trader's, not the viewer's. Rendered only when the
 * org has a US account, so an org without US sees the page as before.
 */
import { useEffect, useState } from 'react';
import { isTerminalUsOrder, LIFECYCLE_LABEL, presentUsPosition, RECEIPT_LABEL, usPositionKey } from '@/lib/polymarketUs/portfolioModel';
import { usFillProgress, usOrderNeedsFillVisibility } from '@/lib/polymarketUs/fillProgress';
import type { UsFillProgress, UsLifecycleState } from '@/lib/polymarketUs/types';
import {
  checkUsAccounts, dash, readJson, traderPositions, usdTextToCents, UsFillMarket, UsFillPrice, UsFillTime, UsRetry,
  UsStatusRegion, US_MANAGER_API as API, type ManagerUsFill as TraderFill, type TraderPositions, type UsAccountCheck,
} from './usManagerReads';

interface TraderOrder {
  intentId: string;
  accountId?: string;
  fillProgress?: UsFillProgress;
  /** marketTitle: the relay's catalogue or stored title (#1026); absent from an older relay. */
  target: { marketSlug: string; outcome: 'yes' | 'no'; marketTitle?: string | null };
  action: 'buy' | 'sell';
  quantity: string;
  outcomePrice: string;
  submissionState: string;
  lifecycle: { state?: string; filledQuantity?: string | null; remainingQuantity?: string | null; observedAtMs?: number } | null;
  createdAtMs: number;
}
type Loaded = {
  key: string;
  /** The page refresh this answer is for; a newer refreshKey means a re-read is in flight. */
  refresh: number;
  accounts: UsAccountCheck;
  positions: TraderPositions | null;
  orders: TraderOrder[] | null;
  fills: TraderFill[] | null;
  complete: boolean;
};

/** The lifecycle word when the venue has reported one, else the receipt word, else the raw code. */
function orderStatus(o: TraderOrder): { label: string; code: string } {
  if (usOrderNeedsFillVisibility(o)) return { label: o.fillProgress?.state === 'clearing' ? 'Fill clearing' : `${o.lifecycle?.state && Object.prototype.hasOwnProperty.call(LIFECYCLE_LABEL, o.lifecycle.state) ? LIFECYCLE_LABEL[o.lifecycle.state as UsLifecycleState] + ' · ' : ''}Holding unconfirmed`, code: o.fillProgress!.state };
  if (o.fillProgress?.state === 'reversed') return { label: 'Fill reversed', code: 'reversed' };
  const life = o.lifecycle?.state;
  if (life && Object.prototype.hasOwnProperty.call(LIFECYCLE_LABEL, life)) return { label: LIFECYCLE_LABEL[life as UsLifecycleState], code: life };
  const receipt = Object.prototype.hasOwnProperty.call(RECEIPT_LABEL, o.submissionState)
    ? RECEIPT_LABEL[o.submissionState as keyof typeof RECEIPT_LABEL] : null;
  return { label: receipt ?? o.submissionState, code: o.submissionState };
}

/** Working orders before finished ones, so within the orders read (the relay's
 *  newest 100) finished history never pushes a working order past the 50-row
 *  cut (#1111); newest placed first inside each group. The sort is stable, so
 *  equal times keep the relay's read order. */
function workingFirst(orders: TraderOrder[]): TraderOrder[] {
  const finished = (o: TraderOrder) => !usOrderNeedsFillVisibility(o) && isTerminalUsOrder(o.lifecycle?.state, o.submissionState);
  return [...orders].sort((a, b) => Number(finished(a)) - Number(finished(b)) || b.createdAtMs - a.createdAtMs);
}

export function UsTraderSection({ traderId, orgQueryParam, refreshKey = 0, onRetry, refreshing = false }: {
  traderId: string;
  orgQueryParam: string;
  /** Bumped by the page's Refresh button; the shown rows stay until the new read lands. */
  refreshKey?: number;
  /** The page's Refresh handler, offered as Retry when a read failed. */
  onRetry?: () => void;
  /** The page's own reads are in flight: Retry (the page Refresh) waits for them. */
  refreshing?: boolean;
}) {
  const key = `${traderId}${orgQueryParam}`;
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const accounts = await checkUsAccounts(orgQueryParam);
      if (accounts !== 'present') {
        if (!cancelled) setLoaded({ key, refresh: refreshKey, accounts, positions: null, orders: null, fills: null, complete: true });
        return;
      }
      const [positions, orders, fills] = await Promise.all([
        readJson(`${API}/traders/${traderId}/positions${orgQueryParam}`),
        readJson(`${API}/traders/${traderId}/orders${orgQueryParam}`) as Promise<{ orders?: TraderOrder[] } | null>,
        readJson(`${API}/traders/${traderId}/fills${orgQueryParam}`) as Promise<{ fills?: TraderFill[]; complete?: boolean } | null>,
      ]);
      if (cancelled) return;
      setLoaded({
        key, refresh: refreshKey, accounts,
        positions: traderPositions(positions, traderId),
        orders: Array.isArray(orders?.orders) ? orders.orders : null,
        fills: Array.isArray(fills?.fills) ? fills.fills : null,
        complete: fills?.complete === true,
      });
    })();
    return () => { cancelled = true; };
  }, [key, traderId, orgQueryParam, refreshKey]);

  const current = loaded?.key === key ? loaded : null;
  // A re-read (the page Refresh, or Retry) is in flight until its answer lands.
  const busy = refreshing || current?.refresh !== refreshKey;
  // Four states, never merged: checking, failed, no US (confirmed), US.
  if (!current) return <UsStatusRegion state="loading" testId="trader-detail-us-status" />;
  if (current.accounts === 'failed') return <UsStatusRegion state="failed" testId="trader-detail-us-status" onRetry={onRetry} busy={busy} />;
  if (current.accounts === 'absent') return null;
  const positionTitles = current.positions?.titles;
  const visibleOrders = current.orders?.map(o => {
    if (!o.accountId || typeof o.lifecycle?.filledQuantity !== 'string' || typeof o.lifecycle.observedAtMs !== 'number') return o;
    return { ...o, fillProgress: usFillProgress({ intentId: o.intentId, target: { ...o.target, label: o.target.marketSlug },
      lifecycle: { state: o.lifecycle.state as UsLifecycleState, filledQuantity: o.lifecycle.filledQuantity,
        remainingQuantity: o.lifecycle.remainingQuantity ?? null, observedAtMs: o.lifecycle.observedAtMs } },
      current.positions?.orderFills.get(o.accountId) ?? [], current.positions?.knownHoldings.has(JSON.stringify([o.accountId, o.target.marketSlug, o.target.outcome])) ?? false) };
  });

  // Market cells are w-full + max-w-0: they take only the width left over and truncate,
  // so no table outgrows its panel on a narrow window (the row title carries the full name).
  return (
    <>
      <div className="bg-card border-border rounded-lg border" data-testid="trader-detail-us-positions">
        <div className="border-border flex items-center justify-between gap-2 border-b px-4 py-2.5">
          <div className="flex items-center gap-2">
            <h2 className="text-foreground text-sm font-medium">Polymarket US Positions</h2>
            {current.positions !== null && !current.positions.complete && (
              <span className="text-muted-foreground text-[10px]">incomplete</span>
            )}
          </div>
          <span className="text-muted-foreground text-[11px]">held contracts only · no US average cost or P&L yet</span>
        </div>
        {current.positions === null ? (
          <p className="px-4 py-6 text-center text-sm text-amber-500 light:text-amber-700">Polymarket US positions could not be read<UsRetry onRetry={onRetry} busy={busy} /></p>
        ) : current.positions.rows.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open Polymarket US positions</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-4 py-2 text-left">Market</th>
                <th className="px-3 py-2 text-center">Side</th>
                <th className="px-3 py-2 text-right">Held</th>
              </tr>
            </thead>
            <tbody>
              {current.positions.rows.map((row) => {
                const view = presentUsPosition(row);
                return (
                  <tr key={`${row.scope.scopeKey}|${usPositionKey(row)}`} className="border-border/40 border-b" title={row.target.marketSlug}>
                    <td className="text-foreground w-full max-w-0 truncate px-4 py-2 text-xs"
                      title={positionTitles?.get(row.target.marketSlug) ?? row.target.marketSlug}>
                      {positionTitles?.get(row.target.marketSlug) ?? row.target.marketSlug}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${row.target.outcome === 'yes'
                        ? 'bg-green-500/20 text-green-400 light:text-green-700' : 'bg-red-500/20 text-red-400 light:text-red-700'}`}>
                        {row.target.outcome.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                      {row.heldQuantity === null
                        ? dash(`Holding not established: ${view.reasons.join(', ') || 'unknown'}`)
                        : view.held}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-card border-border rounded-lg border" data-testid="trader-detail-us-orders">
        <div className="border-border border-b px-4 py-2.5">
          <h2 className="text-foreground text-sm font-medium">Polymarket US Orders</h2>
        </div>
        {current.orders === null ? (
          <p className="px-4 py-6 text-center text-sm text-amber-500 light:text-amber-700">Polymarket US orders could not be read<UsRetry onRetry={onRetry} busy={busy} /></p>
        ) : current.orders.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">No Polymarket US orders</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-4 py-2 text-left">Market</th>
                <th className="px-3 py-2 text-center">Outcome</th>
                <th className="px-3 py-2 text-center">Action</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Remaining</th>
                <th className="px-3 py-2 text-right">Price</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="whitespace-nowrap px-3 py-2 text-right">Placed</th>
              </tr>
            </thead>
            <tbody>
              {workingFirst(visibleOrders ?? current.orders).slice(0, 50).map((o) => {
                const status = orderStatus(o);
                return (
                  <tr key={o.intentId} className="border-border/40 border-b" title={o.target.marketSlug}>
                    <td className="text-foreground w-full max-w-0 truncate px-4 py-2 text-xs" title={o.target.marketTitle ?? o.target.marketSlug}>{o.target.marketTitle ?? o.target.marketSlug}</td>
                    <td className="px-3 py-2 text-center text-xs">{o.target.outcome.toUpperCase()}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{o.action.toUpperCase()}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{o.quantity}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{o.lifecycle?.remainingQuantity ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{usdTextToCents(o.outcomePrice)}¢</td>
                    <td className="whitespace-nowrap px-3 py-2 text-center text-[11px]" title={status.code}>{status.label}</td>
                    <td className="text-muted-foreground whitespace-nowrap px-3 py-2 text-right text-[11px]">
                      {new Date(o.createdAtMs).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-card border-border rounded-lg border" data-testid="trader-detail-us-fills">
        <div className="border-border flex items-center gap-2 border-b px-4 py-2.5">
          <h2 className="text-foreground text-sm font-medium">Recent Polymarket US Fills</h2>
          {current.fills !== null && !current.complete && (
            <span className="text-muted-foreground text-[10px]">incomplete</span>
          )}
        </div>
        {current.fills === null ? (
          <p className="px-4 py-6 text-center text-sm text-amber-500 light:text-amber-700">Polymarket US fills could not be read<UsRetry onRetry={onRetry} busy={busy} /></p>
        ) : current.fills.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">No recent Polymarket US fills</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-4 py-2 text-left">Market</th>
                <th className="px-3 py-2 text-center">Outcome</th>
                <th className="px-3 py-2 text-center">Action</th>
                <th className="px-3 py-2 text-right">Count</th>
                <th className="px-3 py-2 text-right">Price</th>
                <th className="whitespace-nowrap px-3 py-2 text-right">When</th>
              </tr>
            </thead>
            <tbody>
              {current.fills.map((f) => (
                <tr key={f.executionId} className="border-border/40 border-b" title={f.marketSlug ?? 'market disputed'}>
                  <td className="text-foreground w-full max-w-0 truncate px-4 py-2 text-xs" title={f.marketTitle ?? f.marketSlug ?? 'market disputed'}>
                    <UsFillMarket fill={f} />
                  </td>
                  <td className="px-3 py-2 text-center text-xs">{f.outcome === null ? dash('side disputed') : f.outcome.toUpperCase()}</td>
                  <td className="px-3 py-2 text-center text-xs">{f.action === null ? dash('action disputed') : f.action.toUpperCase()}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{f.quantity ?? dash('quantity disputed')}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    <UsFillPrice fill={f} />
                  </td>
                  <td className="text-muted-foreground whitespace-nowrap px-3 py-2 text-right text-[11px]">
                    <UsFillTime fill={f} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
